import React, { useMemo, useState } from "react";

import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { COMPARE_METRICS, MetricDef } from "consts";

import { ValidationRow } from "models";

import { AntModal } from "components";

import { getScenarioColor } from "pages/admin/soilSight/utils/compareUtils.ts";

export const MultipleVersionSideBySideCharts = ({
  csvA,
  csvB,
  metrics = COMPARE_METRICS,
}: {
  csvA: ValidationRow[];
  csvB: ValidationRow[];
  metrics?: MetricDef[];
}) => {
  const locations = useMemo(() => [...new Set(csvB.map((r) => r.location))].sort(), [csvB]);
  const rotations = useMemo(() => [...new Set(csvB.map((r) => r.rotation))].sort(), [csvB]);
  const rotationCrops = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of csvB) if (typeof row.rotationCrops === "string") m.set(row.rotation, row.rotationCrops);
    return m;
  }, [csvB]);
  const scenarios = useMemo(() => [...new Set(csvB.map((r) => r.scenario))].sort(), [csvB]);

  const [location, setLocation] = useState(locations[0] ?? "");
  const [rotation, setRotation] = useState(rotations[0] ?? "");
  const [metricKey, setMetricKey] = useState(metrics[0]!.key);
  const [zoomed, setZoomed] = useState<string | null>(null);

  const metric = metrics.find((m) => m.key === metricKey) ?? metrics[0]!;

  const indexA = useMemo(() => {
    const map = new Map<string, ValidationRow>();
    for (const row of csvA) map.set(`${row.location}|${row.rotation}|${row.scenario}|${row.year}`, row);
    return map;
  }, [csvA]);

  const maxYear = useMemo(() => Math.max(...csvB.map((r) => r.year)), [csvB]);

  const chartDataByScenario = useMemo(() => {
    const result: Record<string, { year: number; a: number | null; b: number | null }[]> = {};
    for (const sc of scenarios) {
      result[sc] = Array.from({ length: maxYear }, (_, i) => {
        const year = i + 1;
        const rowB = csvB.find((r) => r.location === location && r.rotation === rotation && r.scenario === sc && r.year === year);
        const rowA = indexA.get(`${location}|${rotation}|${sc}|${year}`);
        const valB = rowB ? rowB[metric.key] : null;
        const valA = rowA ? rowA[metric.key] : null;
        return { year, a: typeof valA === "number" ? valA : null, b: typeof valB === "number" ? valB : null };
      });
    }
    return result;
  }, [csvB, location, rotation, scenarios, metric.key, maxYear, indexA]);

  const renderChart = (sc: string, height: number, fontSize: number) => {
    const color = getScenarioColor(sc, scenarios.indexOf(sc));
    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={chartDataByScenario[sc]} margin={{ top: 4, right: 16, bottom: 0, left: -8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis dataKey="year" tick={{ fontSize }} />
          <YAxis tick={{ fontSize }} />
          <Tooltip
            formatter={(value, name) => [`${Number(value).toFixed(2)} ${metric.unit}`, name === "a" ? "Version A" : "Version B"]}
            labelFormatter={(l) => `Year ${l}`}
          />
          <Line type="monotone" dataKey="a" stroke={color} strokeWidth={2} dot={false} name="a" strokeDasharray="6 3" />
          <Line type="monotone" dataKey="b" stroke={color} strokeWidth={2} dot={false} name="b" />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div>
      <div className="soil-sight-side-toolbar">
        <select value={location} onChange={(e) => setLocation(e.target.value)} className="soil-sight-native-select" style={{ minWidth: 140 }}>
          {locations.map((location) => (
            <option key={location} value={location}>
              {location}
            </option>
          ))}
        </select>
        <select value={rotation} onChange={(e) => setRotation(e.target.value)} className="soil-sight-native-select" style={{ minWidth: 260 }}>
          {rotations.map((rotationOption) => (
            <option key={rotationOption} value={rotationOption}>
              {rotationCrops.get(rotationOption) ? `${rotationOption} (${rotationCrops.get(rotationOption)})` : rotationOption}
            </option>
          ))}
        </select>
        <select
          value={metricKey}
          onChange={(e) => setMetricKey(metrics.find((metric) => metric.key === e.target.value)?.key ?? metricKey)}
          className="soil-sight-native-select"
          style={{ minWidth: 160 }}
        >
          {metrics.map((metric) => (
            <option key={metric.key} value={metric.key}>
              {metric.label} ({metric.unit})
            </option>
          ))}
        </select>
        <div className="soil-sight-side-legend">
          <span className="soil-sight-side-legend-item">
            <svg width="20" height="4">
              <line x1="0" y1="2" x2="20" y2="2" stroke="#374151" strokeWidth="2" strokeDasharray="6 3" />
            </svg>
            A (dashed)
          </span>
          <span className="soil-sight-side-legend-item">
            <span className="soil-sight-side-legend-solid" />
            B(faded)
          </span>
        </div>
      </div>

      <div className="soil-sight-side-grid">
        {scenarios.map((sc, i) => (
          <div
            key={sc}
            onClick={() => setZoomed(sc)}
            className="soil-sight-side-card"
            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.10)")}
            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
          >
            <div className="soil-sight-side-card-header">
              <span className="soil-sight-side-card-dot" style={{ background: getScenarioColor(sc, i) }} />
              {sc}
            </div>
            {renderChart(sc, 180, 9)}
          </div>
        ))}
      </div>

      <AntModal
        open={!!zoomed}
        onCancel={() => setZoomed(null)}
        afterOpenChange={(open) => {
          if (open) window.dispatchEvent(new Event("resize"));
        }}
        footer={null}
        width="80vw"
        title={zoomed ? `${metric.label} — ${zoomed} — ${location} / ${rotation}` : null}
        styles={{ body: { paddingTop: 8 } }}
      >
        {zoomed && renderChart(zoomed, 420, 12)}
      </AntModal>
    </div>
  );
};
